
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  CheckSquare, 
  Plus, 
  Clock, 
  CheckCircle,
  Building2,
  Users,
  Settings,
  Rocket
} from 'lucide-react';

const HQChecklists = () => {
  const locations = [
    { id: 'austin-west', name: 'Austin West', status: 'active', progress: 100 },
    { id: 'houston-main', name: 'Houston Main', status: 'launching', progress: 75 },
    { id: 'dallas-north', name: 'Dallas North', status: 'planning', progress: 25 },
  ];

  const checklistItems = [
    {
      id: 1,
      title: "Site Selection & Lease Agreement",
      category: "Legal & Contracts",
      status: "completed",
      dueDate: "2024-01-15",
      description: "Secure location and finalize lease terms"
    },
    {
      id: 2,
      title: "Permits & Licensing",
      category: "Legal & Contracts", 
      status: "completed",
      dueDate: "2024-01-20",
      description: "Obtain all required business and medical permits"
    },
    {
      id: 3,
      title: "Interior Design & Construction",
      category: "Build-Out",
      status: "in-progress",
      dueDate: "2024-02-15",
      description: "Complete interior renovation and design"
    },
    {
      id: 4,
      title: "Equipment Procurement & Installation",
      category: "Build-Out",
      status: "in-progress", 
      dueDate: "2024-02-20",
      description: "Order and install all medical and office equipment"
    },
    {
      id: 5,
      title: "Staff Recruitment & Hiring",
      category: "Staffing",
      status: "pending",
      dueDate: "2024-03-01",
      description: "Hire medical director, nurses, and support staff"
    },
    {
      id: 6,
      title: "Staff Training & Certification",
      category: "Staffing",
      status: "pending",
      dueDate: "2024-03-15",
      description: "Complete all required training modules and certifications"
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-emerald-400" />;
      case 'in-progress':
        return <Clock className="h-5 w-5 text-blue-400" />;
      case 'pending':
        return <div className="w-5 h-5 rounded-full border-2 border-slate-400"></div>;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-slate-400"></div>;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'in-progress': return 'bg-blue-500/20 text-blue-200 border-blue-500/30';
      case 'pending': return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Launch Checklists
              </h1>
              <p className="mt-1 text-amber-200">
                Launch checklists per site (clinical & marketing)
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              Create Checklist
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Location Selector */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-amber-400" />
                Select Location
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {locations.map((location) => (
                  <div
                    key={location.id}
                    className="p-4 border border-slate-600 rounded-lg cursor-pointer transition-all hover:border-amber-500/50 bg-slate-700/30"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-white">{location.name}</h3>
                      <Badge className={getStatusColor(location.status)}>
                        {location.status}
                      </Badge>
                    </div>
                    <Progress value={location.progress} className="h-2 mb-2" />
                    <p className="text-sm text-slate-300">{location.progress}% Complete</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Checklist Progress Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <CheckSquare className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Completed</p>
                    <p className="text-2xl font-bold text-white">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <Clock className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">In Progress</p>
                    <p className="text-2xl font-bold text-white">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-slate-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-slate-500/20">
                    <Users className="h-6 w-6 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-200">Pending</p>
                    <p className="text-2xl font-bold text-white">2</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <Rocket className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Progress</p>
                    <p className="text-2xl font-bold text-white">67%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Checklist Items */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Houston Main - Launch Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {checklistItems.map((item) => (
                  <div key={item.id} className="flex items-center space-x-4 p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    {getStatusIcon(item.status)}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white font-medium">{item.title}</h3>
                        <div className="flex items-center space-x-2">
                          <Badge className="bg-purple-500/20 text-purple-200 border-purple-500/30 text-xs">
                            {item.category}
                          </Badge>
                          <Badge className={getStatusColor(item.status)}>
                            {item.status}
                          </Badge>
                        </div>
                      </div>
                      <p className="text-slate-300 text-sm mb-1">{item.description}</p>
                      <p className="text-slate-400 text-xs">Due: {item.dueDate}</p>
                    </div>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                        View Details
                      </Button>
                      {item.status !== 'completed' && (
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                          Update
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQChecklists;
